<template>
  <div id="dummyMap" style="height: 80%; width: 100%; border-radius: 10px;"></div>
  <div style="margin-top: 20px; width: 100%;">
    <h3>📍 장치 목록</h3>
    <ul v-if="dummyMarkers.length > 0" style="width: 100%;">
      <li v-for="(markerData) in dummyMarkers" :key="markerData.id" style="display: flex; width: 100%; margin-bottom: 15px; padding: 10px; border: 1px solid #eee; border-radius: 5px; align-items: center;">
        <p style="width: 200px;"><strong>마커 ID:</strong> {{ markerData.id }}</p>
        <div style="display: flex; width: 300px;">
          <label :for="'deviceId-' + markerData.id" style="width: 50px;">장치 ID:</label>
          <input type="text" :id="'deviceId-' + markerData.id" v-model="markerData.deviceId" placeholder="장치 ID 입력" style="margin-left: 5px; padding: 5px; border: 1px solid #ccc; border-radius: 3px;">
        </div>
        <div style="display: flex; flex-direction: column;">
          <div style="display: flex; width: 300px;">
            <label :for="'lat-' + markerData.id">위도:</label>
            <input type="number" :id="'lat-' + markerData.id" v-model.number="markerData.lat" placeholder="예: 60" style="margin-left: 5px; padding: 5px; border: 1px solid #ccc; border-radius: 3px;">
          </div>
          <div style="display: flex; width: 300px;">
            <label :for="'lng-' + markerData.id">경도:</label>
            <input type="number" :id="'lng-' + markerData.id" v-model.number="markerData.lng" placeholder="예: 60" style="margin-left: 5px; padding: 5px; border: 1px solid #ccc; border-radius: 3px;">
          </div>
        </div>
        <div style="display: flex; width: 300px;">
          <label :for="'transmissionCycle-' + markerData.id">전송 주기 (초):</label>
          <input type="number" :id="'transmissionCycle-' + markerData.id" v-model.number="markerData.transmissionCycle" placeholder="예: 60" style="margin-left: 5px; padding: 5px; border: 1px solid #ccc; border-radius: 3px;">
        </div>
        <button v-if="!markerData.isRunning" @click="startTransmission(markerData.id)" style="padding: 8px 15px; background-color: #28a745; color: white; border: none; border-radius: 5px; cursor: pointer;">
          실행
        </button>
        <button v-else @click="stopTransmission(markerData.id)" style="padding: 8px 15px; background-color: #ffc107; color: white; border: none; border-radius: 5px; cursor: pointer;">
          중지
        </button>
        <button @click="deleteMarker(markerData.id)" style="padding: 8px 15px; background-color: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer;">
          삭제
        </button>
        <p v-if="markerData.isRunning" style="margin-top: 10px; color: #28a745;">
          데이터 전송 중... (다음 전송까지 {{ markerData.remainingTime }}초)
        </p>
        <p v-else-if="markerData.transmissionIntervalId" style="margin-top: 10px; color: #6c757d;">전송 중지됨</p>
      </li>
    </ul>
    <p v-else>지도를 클릭하여 마커를 추가해주세요.</p>
  </div>
</template>

<script setup>
import { defaultZoomLevel, firstMapSetting } from '@/utils/MapSetting';
import { nextTick, onMounted, onUnmounted, ref, toRaw } from 'vue';
import L from 'leaflet'
import { device_list, GET_SERVER_REQUEST, SERVER_REQUEST } from '@/utils/ServerConnect';

const dummyMap = ref(null);
const dummyMarkers = ref([]);
let markerIdCounter = 0;

onMounted(async () => {
  device_list.value = await GET_SERVER_REQUEST('device');
  await nextTick(async () => {
    dummyMap.value = await firstMapSetting('dummyMap', defaultZoomLevel.value);  // 지도 생성
    dummyMap.value?.invalidateSize(); // 지도 크기를 다시 계산

    dummyMap.value.on('click', (e) => {
      const { latlng } = e;
      
      addMarker(latlng);
    })
  })
});

onUnmounted(() => {
  dummyMarkers.value.forEach(markerData => {
    if (markerData.transmissionIntervalId) {
      clearInterval(markerData.transmissionIntervalId);
    }
    if (markerData.countdownIntervalId) {
      clearInterval(markerData.countdownIntervalId);
    }
  })
});

// 새로운 마커를 추가하고 리스트에 반영하는 함수
const addMarker = (latlng) => {
  markerIdCounter++;
  const newMarkerId = `marker-${markerIdCounter}`;

  // Leaflet 마커 생성 및 드래그 가능하도록 설정
  const newMarker = L.marker([latlng.lat, latlng.lng], { draggable: true }).addTo(dummyMap.value);

  newMarker.bindPopup(`<b>새로운 마커!</b><br>위도: ${latlng.lat.toFixed(6)}<br>경도: ${latlng.lng.toFixed(6)}`).openPopup();

  // 마커 드래그 종료 시 위치 업데이트
  newMarker.on('dragend', (event) => {
    const marker = event.target;
    const position = marker.getLatLng();
    const markerData = dummyMarkers.value.find(m => toRaw(m.markerInstance) === marker);

    if (markerData) {
      markerData.lat = position.lat;
      markerData.lng = position.lng;
      // marker.setPopupContent(`<b>새로운 마커!</b><br>위도: ${position.lat.toFixed(6)}<br>경도: ${position.lng.toFixed(6)}`);
    }
  });
  
  dummyMarkers.value.push({
    id: newMarkerId,
    lat: latlng.lat,
    lng: latlng.lng,
    markerInstance: newMarker,
    deviceId: `dummy-${markerIdCounter}`,
    transmissionCycle: 10,
    isRunning: false,
    transmissionIntervalId: null,
    remainingTime: 0,
    countdownIntervalId: null,
  });
};

const addRandomBattery = () => {
  const min = 0;
  const max = 100;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
const addRandomRSRP = () => {
  const min = -120;
  const max = -50;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
const addRandomSpeed = () => {
  const min = 0;
  const max = 5;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// 실행 버튼 클릭 시 실행될 함수
const startTransmission = (markerId) => {
  const markerData = dummyMarkers.value.find(marker => marker.id === markerId);
  if (markerData && markerData.transmissionCycle > 0 && !markerData.isRunning) {
    // 이미 실행 중인 인터벌이 있다면 중지 (방어적 코드)
    if (markerData.transmissionIntervalId) {
      clearInterval(markerData.transmissionIntervalId);
    }
    if (markerData.countdownIntervalId) {
      clearInterval(markerData.countdownIntervalId);
    }

    markerData.isRunning = true;
    markerData.remainingTime = markerData.transmissionCycle;
    console.log(`마커 ID: ${markerId} - 데이터 전송 시작 (주기: ${markerData.transmissionCycle}초)`);

    sendDataToServer(markerData);
    markerData.transmissionIntervalId = setInterval(() => {
      sendDataToServer(markerData);
      markerData.remainingTime = markerData.transmissionCycle;
    }, markerData.transmissionCycle * 1000); // 초 단위를 밀리초로 변환

    // 카운트다운 인터벌 시작 (1초마다 남은 시간 업데이트)
    markerData.countdownIntervalId = setInterval(() => {
      if (markerData.remainingTime > 0) {
        markerData.remainingTime--;
      }
    }, 1000);

  } else if (markerData && (markerData.transmissionCycle <= 0 || !markerData.transmissionCycle)) {
    alert('전송 주기를 0보다 큰 값으로 입력해주세요.');
  }
};
// 데이터 전송 중지 함수
const stopTransmission = (markerId) => {
  const markerData = dummyMarkers.value.find(marker => marker.id === markerId);
  if (markerData && markerData.isRunning) {
    clearInterval(markerData.transmissionIntervalId);
    clearInterval(markerData.countdownIntervalId);

    markerData.isRunning = false;
    markerData.transmissionIntervalId = null; // ID 초기화
    markerData.countdownIntervalId = null;
    markerData.remainingTime = 0;

    console.log(`마커 ID: ${markerId} - 데이터 전송 중지됨.`);
  }
};

const sendDataToServer = async (markerData) => {

  const totalData = {
    device_id: markerData.deviceId,
    location: {
      latitude: markerData.lat,
      longitude: markerData.lng,
    },
    battery_level: addRandomBattery(),
    lteRsrp: addRandomRSRP(),
    speed: addRandomSpeed(),
    reSend: 0,
  }
  const jsonData = JSON.stringify(totalData);
  console.log(totalData);
  await SERVER_REQUEST('location', 'POST', jsonData, false);
};

// 마커 및 리스트 항목 삭제 함수
const deleteMarker = (markerId) => {
  const index = dummyMarkers.value.findIndex(marker => marker.id === markerId);
  if (index !== -1) {
    stopTransmission(markerId);
    // 지도에서 마커 제거
    dummyMarkers.value[index].markerInstance.remove();
    // dummyMarkers 배열에서 해당 항목 제거
    dummyMarkers.value.splice(index, 1);
    console.log(`마커 ID: ${markerId} 삭제됨.`);
  }
};
</script>